import { useState } from "react";

import {
  ClientIcon,
  DiscoveryIcon,
  EnvironmentIcon,
  EvidenceIcon,
  GatewayIcon,
  NextIcon,
  PreviousIcon,
} from "./LandingIcons";

const WORKFLOW_STEPS = [
  {
    title: "Create an isolated 8.1 or 8.3 environment",
    detail: "Keep developer work away from the production Gateway.",
    warning: false,
    Icon: EnvironmentIcon,
  },
  {
    title: "Commission required Gateway resources",
    detail: "May require Gateway Portal",
    warning: true,
    Icon: GatewayIcon,
  },
  {
    title: "Attach and discover the intended Vision Client",
    detail: "Collect current Native project and runtime facts.",
    warning: false,
    Icon: ClientIcon,
  },
  {
    title: "Approve and run the intended suite",
    detail: "Runtime Orchestrator owns ordered execution and result authority.",
    warning: false,
    Icon: DiscoveryIcon,
  },
  {
    title: "Retain evidence and visible blockers",
    detail: "Keep the result, diagnostics, and cleanup receipt together.",
    warning: false,
    Icon: EvidenceIcon,
  },
] as const;

const PROOF_SLIDES = [
  {
    src: "/screens/slide-02-environments.png",
    title: "Environment inventory",
    caption: "Readiness stays separate from runtime proof.",
  },
  {
    src: "/screens/slide-01-dashboard.png",
    title: "Control-plane overview",
    caption: "Environment state and recent evidence stay close to the work.",
  },
  {
    src: "/screens/slide-04-native-suites.png",
    title: "Native Vision suites",
    caption: "Attached-client proof is explicit, not inferred from Gateway health.",
  },
  {
    src: "/screens/slide-06-ag-runtime.png",
    title: "Automation Gateway runtime",
    caption: "Support-resource state is visible without claiming Gateway connection proof.",
  },
  {
    src: "/screens/slide-07-ag-template.png",
    title: "Simulation resources",
    caption: "Templates remain bounded inputs to a commissioned environment.",
  },
] as const;

export default function LandingWorkflow() {
  return (
    <>
      <section className="oc-workflow" id="workflow" aria-labelledby="workflow-title">
        <div className="oc-site-shell">
          <div className="oc-section-intro oc-section-intro--center">
            <p className="oc-eyebrow">A gated workflow</p>
            <h2 id="workflow-title">From isolated environment to retained evidence.</h2>
            <p>
              OrbitCell keeps the steps explicit because support-resource readiness
              is not the same as an Ignition connection&mdash;and a running Gateway
              is not proof of a testable Vision Client.
            </p>
          </div>

          <ol className="oc-workflow-steps">
            {WORKFLOW_STEPS.map(({ title, detail, warning, Icon }, index) => (
              <li key={title}>
                <div className="oc-workflow-step__marker">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <Icon />
                </div>
                <h3>{title}</h3>
                <p className={warning ? "is-warning" : undefined}>{detail}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <ProofGallery />
    </>
  );
}

function ProofGallery() {
  const [activeIndex, setActiveIndex] = useState(0);
  const visibleSlides = [
    PROOF_SLIDES[activeIndex],
    PROOF_SLIDES[(activeIndex + 1) % PROOF_SLIDES.length],
  ];

  const move = (direction: -1 | 1) => {
    setActiveIndex((current) =>
      (current + direction + PROOF_SLIDES.length) % PROOF_SLIDES.length,
    );
  };

  return (
    <section className="oc-proof" id="proof" aria-labelledby="proof-title">
      <div className="oc-site-shell">
        <div className="oc-proof__heading">
          <div className="oc-section-intro">
            <p className="oc-eyebrow">Product proof</p>
            <h2 id="proof-title">The control plane keeps proof close to the work.</h2>
            <p>
              Environment inventory, readiness, authoring, runtime inspection,
              and evidence stay in one product surface&mdash;while each capability
              keeps its current limitation visible.
            </p>
          </div>
          <a className="oc-text-link" href="#readiness">
            Explore current readiness <span aria-hidden="true">&rarr;</span>
          </a>
        </div>

        <div className="oc-proof-gallery" aria-live="polite">
          {visibleSlides.map((slide, index) => (
            <figure className={`oc-proof-card oc-proof-card--${index + 1}`} key={slide.src}>
              <div className="oc-proof-card__image">
                <img src={slide.src} alt="" loading="lazy" decoding="async" />
              </div>
              <figcaption>
                <strong>{slide.title}</strong>
                <span>{slide.caption}</span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="oc-proof-controls">
          <span>
            {String(activeIndex + 1).padStart(2, "0")} /{" "}
            {String(PROOF_SLIDES.length).padStart(2, "0")}
          </span>
          <div className="oc-proof-dots" aria-hidden="true">
            {PROOF_SLIDES.map((slide, index) => (
              <i className={index === activeIndex ? "is-active" : ""} key={slide.src} />
            ))}
          </div>
          <button type="button" onClick={() => move(-1)} aria-label="Previous product view">
            <PreviousIcon />
          </button>
          <button type="button" onClick={() => move(1)} aria-label="Next product view">
            <NextIcon />
          </button>
        </div>
      </div>
    </section>
  );
}
